import { useMemo } from 'react';

import { TokenUsageWithBreakdown } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { EnrichedWorkspace, ModelFilterOption, User, UserFilterOption, WorkspaceFilterOption } from '../types';
import {
  extractModelFilterOptions,
  extractTokenUserFilterOptions,
  extractTokenWorkspaceFilterOptions,
} from '../utils/extractTokenFilterOptions';

/**
 * Result type for prefetched token filter options
 */
export interface TokenFilterOptionsResult {
  /** Available model options for filtering */
  modelOptions: ModelFilterOption[];
  /** Available workspace options for filtering (with resolved display names) */
  workspaceOptions: WorkspaceFilterOption[];
  /** Available user options for filtering (with resolved display names) */
  userOptions: UserFilterOption[];
}

/**
 * Hook to extract filter options from prefetched token usage data
 *
 * Takes the prefetched data for each grouping type and extracts unique
 * filter options with human-readable display names.
 *
 * @param modelData Token usage data grouped by MODEL
 * @param workspaceData Token usage data grouped by WORKSPACE
 * @param userData Token usage data grouped by USER
 * @param workspacesMap Map of workspace UUIDs to enriched workspace objects
 * @param usersMap Map of user UUIDs to user objects
 * @returns Object containing filter options for models, workspaces, and users
 */
export function usePrefetchedTokenFilterOptions(
  modelData: TokenUsageWithBreakdown[] | undefined,
  workspaceData: TokenUsageWithBreakdown[] | undefined,
  userData: TokenUsageWithBreakdown[] | undefined,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>
): TokenFilterOptionsResult {
  // Extract model options from MODEL-grouped data
  const modelOptions = useMemo<ModelFilterOption[]>(() => {
    return extractModelFilterOptions(modelData);
  }, [modelData]);

  // Extract workspace options from WORKSPACE-grouped data
  const workspaceOptions = useMemo<WorkspaceFilterOption[]>(() => {
    return extractTokenWorkspaceFilterOptions(workspaceData, workspacesMap);
  }, [workspaceData, workspacesMap]);

  // Extract user options from USER-grouped data
  const userOptions = useMemo<UserFilterOption[]>(() => {
    return extractTokenUserFilterOptions(userData, usersMap);
  }, [userData, usersMap]);

  return {
    modelOptions,
    workspaceOptions,
    userOptions,
  };
}
